import { useState } from "react";
import { FiChevronLeft, FiChevronRight, FiHome } from "react-icons/fi";
import Dropdown from "../dropdowns/dropdown";

type BrowseHeaderProps = {
  currentPath: string[];
  historyIndex: number;
  historyLength: number;
  onNavigateBack: () => void;
  onNavigateForward: () => void;
  onNavigateToFolder: (path: string[], addToHistory?: boolean) => void;
};

const BrowseHeader = ({
  currentPath,
  historyIndex,
  historyLength,
  onNavigateBack,
  onNavigateForward,
  onNavigateToFolder,
}: BrowseHeaderProps) => {
  const [isEllipsisOpen, setIsEllipsisOpen] = useState(false);
  const showEllipsis = currentPath.length > 4;
  const headSegments = showEllipsis ? currentPath.slice(0, 2) : currentPath;
  const middleSegments = showEllipsis ? currentPath.slice(2, -2) : [];
  const tailSegments = showEllipsis ? currentPath.slice(-2) : [];

  const renderSegment = (label: string, path: string[], key: string) => (
    <div key={key} className="flex items-center">
      <FiChevronRight className="mx-1" size={14} />
      <button
        onClick={() => onNavigateToFolder(path)}
        className="px-2 py-1 rounded transition-colors select-none hover:bg-gray-100"
      >
        {label}
      </button>
    </div>
  );

  return (
    <div className="flex items-center justify-between px-4 py-2 bg-zinc-100 border-b border-gray-200">
      <div className="flex items-center space-x-2">
        <button
          onClick={onNavigateBack}
          disabled={historyIndex === 0}
          className={`p-2 rounded ${
            historyIndex === 0 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100"
          }`}
          title="Back"
        >
          <FiChevronLeft size={18} />
        </button>
        <button
          onClick={onNavigateForward}
          disabled={historyIndex === historyLength - 1}
          className={`p-2 rounded ${
            historyIndex === historyLength - 1
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-gray-100"
          }`}
          title="Forward"
        >
          <FiChevronRight size={18} />
        </button>
        <div className="h-6 w-px mx-1 bg-gray-300" />
        <button
          onClick={() => onNavigateToFolder([])}
          className="p-2 rounded hover:bg-gray-100"
          title="Go to home"
        >
          <FiHome size={18} />
        </button>
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center space-x-1 flex-1 mx-4">
        <button
          onClick={() => onNavigateToFolder([])}
          className="px-2 py-1 rounded transition-colors select-none hover:bg-gray-100"
        >
          Cruises
        </button>
        {headSegments.map((part, index) =>
          renderSegment(part, currentPath.slice(0, index + 1), `head-${index}`)
        )}
        {showEllipsis && (
          <div className="flex items-center">
            <FiChevronRight className="mx-1" size={14} />
            <Dropdown
              label={<span className="text-sm">...</span>}
              isOpen={isEllipsisOpen}
              setIsOpen={setIsEllipsisOpen}
              showCaret={false}
              className="z-[60]"
              buttonStyle="border-0 px-2 py-1 text-sm font-normal text-gray-700 hover:bg-gray-200 rounded-md transition-colors items-center"
              menuClassName="left-0 mt-1 min-w-[220px] z-[70]"
            >
              {middleSegments.map((part, index) => {
                const fullIndex = index + 2;
                const isFirst = index === 0;
                const isLast = index === middleSegments.length - 1;
                return (
                  <button
                    key={`middle-${part}-${fullIndex}`}
                    onClick={() => {
                      onNavigateToFolder(currentPath.slice(0, fullIndex + 1));
                      setIsEllipsisOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                      isFirst ? "rounded-t-md" : ""
                    } ${isLast ? "rounded-b-md" : ""}`}
                  >
                    {part}
                  </button>
                );
              })}
            </Dropdown>
          </div>
        )}
        {showEllipsis &&
          tailSegments.map((part, index) => {
            const fullIndex = currentPath.length - 2 + index;
            return renderSegment(
              part,
              currentPath.slice(0, fullIndex + 1),
              `tail-${fullIndex}`
            );
          })}
      </div>
    </div>
  );
};

export default BrowseHeader;
